/**
 * Добавление счётчиков — разметка 1:1 с legacy admin_counters.tpl
 * (блоки .addtransaction / .counterslist в стиле newapproach: .clhead/.clbody/.clcollapse,
 * таблица .clbcounterstable).
 *
 * Исключение — окно подтверждения удаления: самодельный модал legacy
 * (.removemodal/.removemodalbk) заменён общим <Modal> (закрытие по фону и Esc,
 * блокировка прокрутки, возврат фокуса). Текст и действие те же.
 *
 * Inline-JS шаблона перенесён в обработчики React с сохранением поведения:
 *  - оба блока при загрузке СВЁРНУТЫ (legacy вызывает clcollapse() для каждого .clcollapse),
 *    стрелка fa-angle-double-down / fa-angle-double-up, у свёрнутого .clhead — нижняя граница;
 *  - «Добавить счетчик» → POST, затем перезагрузка списка и сообщение
 *    «Счетчик {номер} добавлен» в .addcountererrors;
 *  - иконка корзины → модал «Подтверждение удаления» → «Удалить» (мягкое удаление, is_active=false).
 *
 * API (legacy getallcounters/addcounter/delcounterany, Admin.php:6552-6584):
 *   GET /api/admin/counters, POST /api/admin/counters {counter}, DELETE /api/admin/counters/{id}.
 */

import { useEffect, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { Modal } from '@/components/Modal'
import { useApi } from '@/lib/useApi'

/** Счётчик (t_counter). */
interface CounterRow {
  id: number
  number: string
}

/** admin.css/newapproach.css — так же, как их подключает admin.tpl (в index.html их нет). */
function useAdminCss(): void {
  useEffect(() => {
    for (const href of ['/assets/css/admin.css', '/assets/css/newapproach.css']) {
      if (document.head.querySelector(`link[href="${href}"]`) !== null) continue
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
    }
  }, [])
}

export function AdminCountersPage() {
  useAdminCss()

  const { data, loading, reload } = useApi<CounterRow[]>('/api/admin/counters')
  const counters = data ?? []

  // legacy: при загрузке страницы clcollapse() схлопывает оба блока
  const [addCollapsed, setAddCollapsed] = useState(true)
  const [listCollapsed, setListCollapsed] = useState(true)

  const [counterValue, setCounterValue] = useState('')
  const [message, setMessage] = useState('') // .addcountererrors
  const [busy, setBusy] = useState(false)
  const [removeId, setRemoveId] = useState<number | null>(null)

  async function addCounter(): Promise<void> {
    setBusy(true)
    setMessage('')
    try {
      await api.post<{ id: number; number: string }>('/api/admin/counters', { counter: counterValue })
      setMessage(`Счетчик ${counterValue} добавлен`)
      setCounterValue('')
      reload()
    } catch (e: unknown) {
      setMessage(e instanceof ApiError ? e.message : 'Не удалось добавить счетчик')
    } finally {
      setBusy(false)
    }
  }

  async function deleteCounter(id: number): Promise<void> {
    setBusy(true)
    try {
      await api.delete<{ id: number }>(`/api/admin/counters/${id}`)
      setRemoveId(null)
      reload()
    } catch (e: unknown) {
      setMessage(e instanceof ApiError ? e.message : 'Не удалось удалить счетчик')
      setRemoveId(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {/* legacy .removemodal (280px, по центру) — теперь общий Modal */}
      <Modal
        open={removeId !== null}
        onClose={() => setRemoveId(null)}
        title="Подтверждение удаления"
        dialogStyle={{ width: 280, maxWidth: '100%' }}
        footer={
          <button
            type="button"
            className="btn btn-default delcounter"
            onClick={() => {
              if (removeId !== null) void deleteCounter(removeId)
            }}
          >
            Удалить
          </button>
        }
      >
        <div style={{ textAlign: 'center' }}>Подтвердить удаление счетчика</div>
      </Modal>

      <div className="row">
        <div className="col-md-12 nompnmob">
          <div className="addtransaction">
            <div className="clhead" style={{ borderBottom: addCollapsed ? '1px solid #ccc' : '0px' }}>
              <div className="clhtitle clcollapse" onClick={() => setAddCollapsed((value) => !value)}>
                Добавление счетчика{' '}
                <span className="collapsespan">
                  <i className={addCollapsed ? 'fas fa-angle-double-down' : 'fas fa-angle-double-up'} />
                </span>
              </div>
            </div>
            <div className="clbody" style={{ display: addCollapsed ? 'none' : 'block' }}>
              <div className="addcountererrors">{message}</div>
              <div className="counteaddflex">
                <input
                  type="text"
                  className="countervalue"
                  placeholder="Номер счетчика"
                  value={counterValue}
                  onChange={(event) => setCounterValue(event.target.value)}
                />
                <div className="addcounter" onClick={() => void addCounter()}>
                  {busy ? 'Добавление…' : 'Добавить счетчик'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="counterslist nomarginleft col-lg-12 col-md-12">
          <div className="clhead" style={{ borderBottom: listCollapsed ? '1px solid #ccc' : '0px' }}>
            <div
              className="clhtitle clcollapse"
              data-cltype="cl"
              onClick={() => setListCollapsed((value) => !value)}
            >
              Счетчики{' '}
              <span className="collapsespan">
                <i className={listCollapsed ? 'fas fa-angle-double-down' : 'fas fa-angle-double-up'} />
              </span>
            </div>
          </div>
          <div className="clbody" style={{ display: listCollapsed ? 'none' : 'block' }}>
            <table className="clbcounterstable">
              <thead>
                <tr className="clbcontactitemth">
                  <th className="counternbm">№</th>
                  <th className="countername">Номер счетчика</th>
                  <th className="countercontrol">Управление</th>
                </tr>
              </thead>
              <tbody className="bodycounterstable">
                {loading && (
                  <tr>
                    <td style={{ textAlign: 'center' }} colSpan={3}>
                      Загрузка данных...
                    </td>
                  </tr>
                )}
                {!loading &&
                  counters.map((counter, index) => (
                    <tr key={counter.id} className="clbcontactitem">
                      <td className="counternbm">{index + 1}</td>
                      <td className="countername">{counter.number}</td>
                      <td className="countercontrol">
                        <div className="countercontrolflex">
                          <div className="counterremove" onClick={() => setRemoveId(counter.id)}>
                            <i className="far fa-trash-alt" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot className="footcounterstable" />
            </table>
          </div>
        </div>
      </div>
    </>
  )
}
